import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { useDispatch, useSelector } from "react-redux";

import { toast } from "react-toastify";

import { UserData } from "@web-solutions/base-app/api/soula-api/types"
import { createUser } from "@web-solutions/base-app/api/soula-api"

import { SOLIDGATE_ERROR_CODES } from "@web-solutions/core/constants/errors";
import type { PaymentError } from "@web-solutions/core/interfaces/errors";

import type { SubscribeOnOneClickParams } from "@web-solutions/core/store/billing/types";
import Analytics from "@web-solutions/module-analytics";

import { PaymentSystem, Provider } from "@web-solutions/react-billing";

import { useAppSelector } from "@web-solutions/store/types";

import { ModeGooglePay } from "core/constants/billing";
import { EVENT_ACTION } from "core/constants/general";
import { ROUTES } from "core/constants/routes";

import { useRemoteConfig } from "core/hooks/use-remote-config";
import {
  STRIPE_INTRO_PAYMENT,
  confirmStripePaymentIntent,
  enrichStripeIntroProduct,
} from "core/utils/stripe-payment";
import {
  useShowApplePay,
  useShowGooglePay,
  useShowMercadoPago,
  useShowPayPal,
} from "core/hooks/use-show-payment-method";
import {
  OrderDetails,
  PaddleConfig,
  type Subscription,
} from "core/interfaces/billing";
import type { PaymentModalTitle } from "core/interfaces/payment-modal";

import BasePaymentModal from "core/payment/base";
import {
  extractDeclineCode,
  extractResponseCode,
} from "core/payment/utils/extract-decline-code";
import {
  PaymentErrorState,
  getHttpStatus,
  getPaymentErrorAnalyticsParams,
  resolvePaymentErrorState,
} from "core/payment/utils/payment-error-state";
import ThreeDSecure, {
  type RecurlyError,
} from "core/payment/components/threeDSecure";
import {
  applePayOrder,
  checkRedirectResults,
  confirmStripeSubscriptionPayment,
  createCustomer,
  createOneClickPayment,
  handleErrorPurchase,
  handleSuccessPurchase,
  initOrders,
  setInjectedPaymentMethod,
  setIsSubmitByCard,
  setLoading,
  setPostCode,
  subscribe,
  switchToReservePaymentSystem,
  tailoredpayCheckout,
} from "core/store/billing/actions";

import {
  type ProductDetails,
  selectLoading,
  selectNoFundsOfferEndDate,
  selectPaddleConfig,
  selectPaymentSystem,
  selectPostcode,
  selectProducts,
} from "core/store/billing/selectors";
import { Preloader } from "core/ui-elements";
import PaymentProcessingOverlay from "core/payment/components/payment-processing-overlay";

import { nextStep, setCurrentStep } from "core/upsale/store";

import { submitCancelSubscription } from "core/upsale/store/actions";

//@ts-ignore
import { handlePurchaseThunk } from "core/upsale/store/thunks/purchase";

//@ts-ignore
import { processEmail } from "src/store/profile/actions";

import ErrorPopup from "./components/error-popup";
import {
  SubscribeOnOneClick,
  type SubscribeOnOneClickRef,
} from "./components/subscribe-on-one-click";

import { CardFormLayout } from "./constants";

export type Options = {
  orderDetails: OrderDetails | null;
  config: {
    email: any;
    postcode: string;
  } & PaddleConfig;
  layout: CardFormLayout;
  applePayButtonParams: {
    color: "black" | "white" | "white-outline";
    enabled: boolean;
    disabled: boolean;
    containerId: string;
  };
  googlePayButtonParams: {
    color: "black" | "white";
    type: string;
    mode: ModeGooglePay;
    disabled: boolean;
    containerId: string;
  };
  formParams: {
    autoFocus: boolean;
  };
};

export interface PaymentProcessorRef {
  showPaymentPopup: () => void;
  closePaymentPopup: () => void;
  processFormData: (data: any) => void;
  subscribeOnOneClick: (data: SubscribeOnOneClickParams) => void;
}

export interface PaymentProcessorProps {
  activeProduct?: ProductDetails;
  fullMode?: boolean;
  shownByDefault?: boolean;
  orderDetails: OrderDetails | null;
  isOnlyBaseModal?: boolean;
  paymentModalTitle?: PaymentModalTitle;
  paymentModalSubtitle?: React.ReactNode;
  isOnlyCard?: boolean;
  isOnlyPayPal?: boolean;
  errorPopupWithPayPal?: boolean;
  hidePayPal?: boolean;
  onSuccess: (purchase: Subscription) => void;
  onError?: (error: any) => void;
  onClose?: () => void;
  onSubmit?: () => void;
  /**
   * Upsale-only: take over the charge once the window has collected a payment
   * method. The offer screen knows the amount and the product and owns the
   * payment-failed sheet, so the window stops at tokenization instead of
   * running the paywall's intro + subscription pair.
   */
  onUpsaleCardCollected?: (formData: any) => void | Promise<any>;
  onSubscribeOnOneClickError?: (error: PaymentError) => void;
  isPurchaseProgress?: boolean;
  upsaleTrigger?: string;
  isUpsaleOneTimePayment?: boolean;
  onSaveTokenOneTime?: (tokenId: string) => void;
  oneTimeRecurlyId?: string;
  subscribeRecurlyId?: string;
  isOnlyOneTimePurshaseUpsale?: boolean;
  isRedirectToLastScreen?: boolean;
  showModalOnce?: boolean;
}

const APP_NAME=process.env.REACT_APP_APP_NAME
const STRIPE_SUBSCRIPTION_ID_STORAGE_KEY = 'stripe_subscription_id';

const getStripeSubscriptionId = (subscription?: Partial<Subscription> | null) =>
  subscription?.subscription_id || subscription?.stripeSubscriptionId || subscription?.id;

const saveStripeSubscriptionId = (subscriptionId?: string | null) => {
  if (!subscriptionId) return;

  sessionStorage.setItem(STRIPE_SUBSCRIPTION_ID_STORAGE_KEY, subscriptionId);
};

const getSavedStripeSubscriptionId = () =>
  sessionStorage.getItem(STRIPE_SUBSCRIPTION_ID_STORAGE_KEY);

const clearSavedStripeSubscriptionId = (subscriptionId?: string | null) => {
  if (!subscriptionId || getSavedStripeSubscriptionId() === subscriptionId) {
    sessionStorage.removeItem(STRIPE_SUBSCRIPTION_ID_STORAGE_KEY);
  }
};

const getStripePaymentIntentStatus = (payment: any) =>
  payment?.payment_intent_status || payment?.status || payment?.data?.status || payment?.one_time_payment?.status;

const getStripePaymentIntentClientSecret = (payment: any) =>
  payment?.client_secret || payment?.clientSecret;

// Pull the real failure details out of a one-time-payment response so the
// error popup can show a meaningful code/message instead of a generic stub.
const getStripePaymentError = (payment: any) =>
  payment?.error || payment?.last_payment_error || payment?.data?.error;

const buildStripePaymentError = (
  payment: any,
  fallbackMessage: string,
  httpStatus?: number,
) => {
  const err = getStripePaymentError(payment);
  return {
    // `extractResponseCode` walks the response the same way the decline reason
    // is walked. The three shapes `getStripePaymentError` knows don't cover a
    // 200 with an unfinished intent, where the code sits deeper — that left
    // `code` empty on events whose `decline_code` came through fine.
    code:
      err?.code ||
      extractResponseCode(payment) ||
      err?.decline_code ||
      payment?.payment_intent_status ||
      payment?.status,
    // Kept alongside `code` so the decline reason survives into the error events
    // even when `code` is the umbrella `card_declined` — this object replaces the
    // response, so anything not copied here is lost to the analytics downstream.
    decline_code: err?.decline_code || extractDeclineCode(payment, PaymentSystem.STRIPE),
    message: err?.message || fallbackMessage,
    entity: err?.entity,
    paymentSystem: PaymentSystem.STRIPE,
    // Same reason: the transport status has to be carried across explicitly or
    // the error events lose it (see `getHttpStatus`).
    __http_status: httpStatus,
  };
};

const isStripeIntroProduct = (product?: ProductDetails) =>
  Boolean(product?.isOneTimePurchase || product?.id === STRIPE_INTRO_PAYMENT.priceId || product?.period === 'ONETIME');

/**
 * How many times the subscription step is retried on its own after the intro
 * charge already went through. The intro is not charged again, so a temporary
 * glitch on the second call is worth absorbing silently instead of showing a
 * popup the user has to act on.
 */
const SUBSCRIPTION_RETRY_ATTEMPTS = 2;

const SUBSCRIPTION_RETRY_DELAY_MS = 1200;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * One click on "pay" is two requests: POST /one-time-payment charges the trial,
 * then POST /subscriptions creates the subscription. When the second one fails,
 * the trial is already paid — so we remember that here and the next attempt
 * sends only the subscription request. Without this the retry charges the trial
 * a second time and takes the money twice.
 *
 * localStorage rather than component state so it survives a page reload, and
 * rather than sessionStorage so it survives the tab being closed and reopened.
 */
const STRIPE_INTRO_PAID_STORAGE_KEY = 'stripe_intro_paid';

/**
 * A stale mark would hand out a subscription without charging the trial, so it
 * expires. A funnel session never spans a day.
 */
const STRIPE_INTRO_PAID_TTL_MS = 24 * 60 * 60 * 1000;

type StripeIntroPaidMark = {
  productCode: string;
  paymentIntentId?: string;
  at: number;
};

/** The remembered trial payment, or null if there isn't a usable one. */
const readStripeIntroPaidMark = (productCode?: string): StripeIntroPaidMark | null => {
  if (!productCode) return null;

  try {
    const raw = localStorage.getItem(STRIPE_INTRO_PAID_STORAGE_KEY);
    if (!raw) return null;

    const mark: StripeIntroPaidMark = JSON.parse(raw);

    // A mark for a different product says nothing about this one.
    if (mark?.productCode !== productCode) {
      return null;
    }

    if (!mark.at || Date.now() - mark.at > STRIPE_INTRO_PAID_TTL_MS) {
      localStorage.removeItem(STRIPE_INTRO_PAID_STORAGE_KEY);
      return null;
    }

    return mark;
  } catch {
    return null;
  }
};

const saveStripeIntroPaidMark = (productCode?: string, paymentIntentId?: string) => {
  if (!productCode) return;

  try {
    localStorage.setItem(
      STRIPE_INTRO_PAID_STORAGE_KEY,
      JSON.stringify({ productCode, paymentIntentId, at: Date.now() } as StripeIntroPaidMark),
    );
  } catch {
    // Private mode / storage full — the retry will just charge the trial again,
    // which is the behaviour we had before.
  }
};

const clearStripeIntroPaidMark = () => {
  try {
    localStorage.removeItem(STRIPE_INTRO_PAID_STORAGE_KEY);
  } catch {}
};

/**
 * A Stripe payment intent is only money in the bank at `succeeded`. Anything
 * else — `requires_action` (the user closed the bank window), `requires_payment_method`
 * (the confirmation failed), `processing` — is not a purchase yet.
 */
const isSettledPaymentIntentStatus = (status?: string) => status === 'succeeded';

export const PaymentProcessor = React.forwardRef<
  PaymentProcessorRef,
  PaymentProcessorProps
>(
  (
    {
      activeProduct,
      fullMode,
      shownByDefault,
      orderDetails,
      paymentModalTitle,
      paymentModalSubtitle,
      isOnlyBaseModal,
      isOnlyCard,
      isOnlyPayPal,
      errorPopupWithPayPal,
      hidePayPal,
      onSuccess,
      onClose,
      onError,
      onSubmit,
      onUpsaleCardCollected,
      onSubscribeOnOneClickError,
      isPurchaseProgress,
      upsaleTrigger,
      onSaveTokenOneTime,
      isUpsaleOneTimePayment,
      isOnlyOneTimePurshaseUpsale,
      subscribeRecurlyId,
      oneTimeRecurlyId,
      isRedirectToLastScreen,
      showModalOnce
    },
    ref,
  ) => {
    const dispatch = useDispatch<any>();

    const socRef = useRef<SubscribeOnOneClickRef>(null);

    const [isOpenPaymentPopup, setIsOpenPaymentPopup] =
      useState(!!shownByDefault);
    // Locks the whole page with a full-screen loader from the moment a payment
    // method is authorized (Apple Pay / Google Pay sheet or bank 3-D Secure
    // window closes) until the terminal success/error, so the user can't click
    // the still-visible payment form and submit twice while it's processing.
    const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
    const [paymentData, setPaymentData] = useState<any>(null);
    const [tokenThreeDSecure, setTokenThreeDSecure] = useState(null);
    const [errorCode, setErrorCode] = useState<string>();
    const [errorState, setErrorState] = useState<PaymentErrorState>();
    // How the user was paying when it failed. Needed to tell a typed card from
    // Apple Pay / Google Pay: a wallet user never entered card details, so the
    // "check your card details" state must never be shown to them.
    const submittedMethodRef = useRef<string | undefined>(undefined);

    const paddleConfig = useSelector(selectPaddleConfig);
    const postcode = useSelector(selectPostcode);
    const noFundsOfferEndDate = useSelector(selectNoFundsOfferEndDate);
    const isPending = useSelector(
      (state: any) => state.billing.pending || state.billing.orderPending,
    );
    const isLoadingBilling = useSelector(selectLoading);
    const products = useSelector(selectProducts);
    const isLoadingApp = useSelector((state: any) => !state.app.loaded);
    //@ts-ignore
    const email = useSelector((state) => state.profile.email);
    //@ts-ignore
    const steps = useSelector((state) => state.upsale.steps);
    const selectedAnswers = useAppSelector(
      (state) => state.quiz.selectedAnswers,
    );
    const [errorMessage, setErrorMessage] = useState<string>();

    const [isOpenErrorPopup, setIsOpenErrorPopup] = useState(false);

    const {
      modeApplePay,
      modeGooglePay,
      modeCardForm,
      cardFormLayout,
      errorPopup,
      defaultInjectedPaymentMethod,
      isCvvTooltip,
      isInjectedPaymentMethodOnPage,
      noFundsOffer,
      showPaymentProcessingOverlay,
      paymentSystemReserve,
    } = useRemoteConfig();

    const paymentSystem = useSelector(selectPaymentSystem);
    // Analytics needs to tell a failed main purchase from a failed upsale —
    // without it upsale failures are invisible in the data.
    const errorPlace = isUpsaleOneTimePayment ? 'upsale' : 'purchase';
    const isStripePayment =
      paymentSystem === PaymentSystem.STRIPE || Boolean(activeProduct?.stripeId);
    // Don't force base modal for Stripe — body/index.tsx routes Stripe to
    // SIMPLE modalType so SimpleView can render the intro/subscription
    // price breakdown.
    const useBasePaymentModal = isOnlyBaseModal;
    const stripeSubscriptionProduct = useMemo(() => {
      if (!isStripePayment) {
        return activeProduct;
      }

      // An upsale is a plain one-time charge: there is no trial paired with a
      // subscription, so the offer's own product is what the window must show.
      // Falling through would graft the paywall's intro breakdown (or the
      // paywall subscription price itself) onto the offer.
      if (isUpsaleOneTimePayment) {
        return activeProduct;
      }

      // If activeProduct is already the intro/ONETIME product, use its amount
      let introAmount: number | undefined;
      if (isStripeIntroProduct(activeProduct)) {
        introAmount = Number(activeProduct?.amount);
      } else {
        // Otherwise try to find intro product in products list
        const introProduct = products.find((p) => isStripeIntroProduct(p));
        introAmount = introProduct ? Number(introProduct.amount) : undefined;
      }

      const baseProduct = activeProduct && !isStripeIntroProduct(activeProduct)
        ? activeProduct
        : products.find((product) => !isStripeIntroProduct(product)) || activeProduct;

      // Inject intro pricing ($0.99 + discount/savedPrice) so SimpleView and
      // ApplePay see the breakdown — Stripe's subscription price alone doesn't
      // carry trial info (we charge intro via a separate one-time payment).
      return enrichStripeIntroProduct(baseProduct, introAmount) || baseProduct;
    }, [activeProduct, isStripePayment, isUpsaleOneTimePayment, products]);

    const showPayPal = useShowPayPal(activeProduct);
    const showApplePay = useShowApplePay();
    const showGooglePay = useShowGooglePay();
    const showMercadoPago =
      useShowMercadoPago() && !activeProduct?.isOneTimePurchase;

    useEffect(() => {
      if (!isLoadingBilling) {
        if (window.location.pathname === ROUTES.VERIFY_3DS_SUCCESS) {
          const subscriptionId = getSavedStripeSubscriptionId();

          if (subscriptionId) {
            dispatch(confirmStripeSubscriptionPayment(subscriptionId)).finally(() => {
              clearSavedStripeSubscriptionId(subscriptionId);
            });
          }
        }

        const search = new URLSearchParams(window.location.search);
        const isReturningFromPaymentRedirect =
          search.get("success") === PaymentSystem.PADDLE ||
          search.get("success") === "mercadopago" ||
          search.get("error") === "mercadopago";

        if (isReturningFromPaymentRedirect) {
          // Returning from a full-page redirect (Paddle / MercadoPago) with the
          // payment already authorized — lock the page while checkRedirectResults()
          // resolves the subscription, so the still-mounted payment form can't be
          // submitted again before the success redirect.
          setIsPaymentProcessing(true);
        }

        dispatch(checkRedirectResults()).then(
          (p: Subscription | null) => {
            if (p) {
              handlePaymentSuccess(p);
            }
          },
          (error: any) => {
            // Resolution failed — unlock so the user can retry / see the error.
            setIsPaymentProcessing(false);
            Analytics.trackEvent(
              "modal_payment",
              "error",
              getPaymentErrorAnalyticsParams(error, {
                paymentSystem: error?.paymentSystem || paymentSystem,
                method: submittedMethodRef.current,
                place: errorPlace as 'purchase' | 'upsale',
              }),
            );
          },
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingBilling]);

    const options: Options = useMemo(
      () => ({
        orderDetails,
        config: Object.assign({ email, postcode }, paddleConfig),
        layout: cardFormLayout,
        applePayButtonParams: {
          color: "black",
          enabled: !process.env.REACT_APP_APPLE_PAY_H2H,
          mode: modeApplePay,
          disabled:
            !email &&
            paymentSystem !== PaymentSystem.SOLIDGATE &&
            paymentSystem !== PaymentSystem.PADDLE,
          containerId: "container-solidgate-apple-pay",
        },
        googlePayButtonParams: {
          color: "black",
          type: "plain",
          mode: modeGooglePay,
          disabled:
            !email &&
            paymentSystem !== PaymentSystem.SOLIDGATE &&
            paymentSystem !== PaymentSystem.PADDLE,
          containerId: "container-solidgate-google-pay",
        },
        formParams: {
          autoFocus: false,
        },
        isCvvTooltip,
      }),
      [
        orderDetails,
        email,
        postcode,
        paddleConfig,
        cardFormLayout,
        paymentSystem,
        modeApplePay,
        modeGooglePay,
        isCvvTooltip,
      ],
    );

    const handleClosePaymentPopup = useCallback(() => {
      Analytics.trackEvent("modal_payment", EVENT_ACTION.CLOSE, { paymentSystem });
      setIsPaymentProcessing(false);
      setIsOpenPaymentPopup(false);
      onClose && onClose();
      if (isInjectedPaymentMethodOnPage) {
        dispatch(setInjectedPaymentMethod(defaultInjectedPaymentMethod));
      }
    }, [
      defaultInjectedPaymentMethod,
      dispatch,
      isInjectedPaymentMethodOnPage,
      onClose,
      paymentSystem,
    ]);

    const handlePaymentSuccess = useCallback(
      async (purchase: Subscription) => {
        Analytics.trackEvent("modal_payment", "success", {
          paymentSystem: purchase?.paymentSystem || paymentSystem,
        });
        onSuccess(purchase);
      },
      [onSuccess, paymentSystem],
    );

    const handleCardPaymentSuccess = useCallback(
      async (purchase: Subscription) => {
        if (purchase.paymentSystem === PaymentSystem.PADDLE) {
          const em = purchase.email || email;
          dispatch(processEmail(em));
          // we do not handle purchase here because purchase is handled at success-url
        } else {
          if (activeProduct && !activeProduct.isOneTimePurchase) {
            dispatch(subscribe({ subscription_id: purchase.transaction_id }));
          }
          await dispatch(
            handleSuccessPurchase({
              ...purchase,
              isOneTimePurchase: activeProduct?.isOneTimePurchase,
              price_id: activeProduct?.id,
            }),
          );
          setIsOpenPaymentPopup(false);
          setTimeout(() => {
            handlePaymentSuccess(purchase);
          }, 1);
        }
      },
      [
        activeProduct?.id,
        activeProduct?.isOneTimePurchase,
        dispatch,
        email,
        handlePaymentSuccess,
      ],
    );

    const handleCardPaymentError = useCallback(
      (
        error: PaymentError,
        options?: { forcePopup?: boolean; state?: PaymentErrorState },
      ) => {
        // Payment failed — unlock the page so the user can retry / see the error.
        setIsPaymentProcessing(false);

        const errorContext = {
          paymentSystem: error?.paymentSystem || paymentSystem,
          method: submittedMethodRef.current,
          place: errorPlace as 'purchase' | 'upsale',
        };
        // `options.state` / `error.paymentErrorState` let a caller pin the state
        // it wants shown — used when the intro charge went through but the
        // subscription step kept failing: the reason is technical, yet the way
        // out is a different card.
        const resolvedState =
          options?.state ||
          (error as any)?.paymentErrorState ||
          resolvePaymentErrorState(error, errorContext);
        const errorAnalytics = {
          ...getPaymentErrorAnalyticsParams(error, errorContext),
          error_state: resolvedState,
        };

        Analytics.trackEvent("modal_payment", "error", errorAnalytics);

        // Our side is at fault (gateway not configured, SDK never loaded), so
        // there is nothing for the user to fix. Switch to the reserve payment
        // system WITHOUT the error popup and without a toast — swapping providers
        // must be invisible to the user.
        if ((error as any)?.isConfigurationError) {
          dispatch(handleErrorPurchase(error, { noToast: true }));
          dispatch(switchToReservePaymentSystem());
          onError && onError(error);
          return;
        }

        const isInsufficientFundsErrorCode =
          error?.code === SOLIDGATE_ERROR_CODES.INSUFFICIENT_FUNDS;
        const isSONoFundsTriggered =
          isInsufficientFundsErrorCode &&
          noFundsOffer.enabled &&
          !noFundsOfferEndDate &&
          error?.entity === "form";

        if (!activeProduct?.isOneTimePurchase && isSONoFundsTriggered) {
          setIsOpenPaymentPopup(false);

          dispatch(handleErrorPurchase(error, { noToast: true }));

          if (error?.paymentSystem === PaymentSystem.SOLIDGATE) {
            dispatch(initOrders());
          }

          onError && onError(error);
          return;
        }

        // Swapping the payment provider must be invisible: the user did nothing
        // wrong and has nothing to fix, so a "billing issue / update your card"
        // popup would be misleading. Retry silently on the reserve system, and
        // keep the error popup for the case where there is nowhere left to move
        // (no reserve configured, or we are already on it).
        const currentPaymentSystem = error?.paymentSystem || paymentSystem;
        const willSwitchToReserve =
          !isInsufficientFundsErrorCode &&
          !!paymentSystemReserve &&
          paymentSystemReserve !== currentPaymentSystem;

        if (willSwitchToReserve) {
          dispatch(handleErrorPurchase(error, { noToast: true }));
          dispatch(switchToReservePaymentSystem());
          onError && onError(error);
          return;
        }

        setErrorCode(error?.code);
        setErrorMessage(error?.message);
        setErrorState(resolvedState);

        // There is nowhere left to switch, so the user has to be told what
        // happened. Every failed payment gets the popup with a reason: it used
        // to be gated on `errorPopup.enabled` and skipped for one-time
        // products, which is why a failed bank confirmation showed nothing but
        // a toast with the raw server text. The upsale is the exception — it
        // owns its own payment-failed sheet, so the popup must not stack on it.
        if (!isUpsaleOneTimePayment) {
          Analytics.trackEvent("error_popup", "open", errorAnalytics);
          // Leave the payment window open underneath: the popup covers part of
          // the screen, and closing it puts the user back on the form with the
          // card they already typed still there.
          setIsOpenErrorPopup(true);
        }
        // The popup (or the upsale's sheet) carries the reason now, so the toast
        // with the raw provider message would only duplicate it.
        dispatch(handleErrorPurchase(error, { noToast: true }));
        if (error?.paymentSystem === PaymentSystem.SOLIDGATE) {
          dispatch(initOrders());
        }

        onError && onError(error);
      },
      [
        noFundsOffer.enabled,
        noFundsOfferEndDate,
        activeProduct?.isOneTimePurchase,
        errorPlace,
        isUpsaleOneTimePayment,
        dispatch,
        onError,
        paymentSystem,
        paymentSystemReserve,
      ],
    );

    const onActionTokenExchanged = useCallback(
      async (actionToken: { id: any }) => {
        setTokenThreeDSecure(null);

        if (isUpsaleOneTimePayment && oneTimeRecurlyId) {
          try {
            dispatch(setLoading(true));
            const purchaseResult = await dispatch(
              handlePurchaseThunk({ recurlyId: oneTimeRecurlyId }),
            );

            if (purchaseResult.meta.requestStatus === "fulfilled") {
              if (isOnlyOneTimePurshaseUpsale) {
                isRedirectToLastScreen
                  ? dispatch(setCurrentStep(steps.length))
                  : dispatch(nextStep());
              }

              if (subscribeRecurlyId) {
                await dispatch(submitCancelSubscription(subscribeRecurlyId));
                await dispatch(
                  subscribe({ ...processFormData, price_id: subscribeRecurlyId }),
                ).then(() => {
                  dispatch(nextStep());
                });
                Analytics.trackEvent("step", "completed", {
                  upsaleID: "SKIP_TRIAL",
                  result: "upsale_selected",
                });
              }
            }
          } finally {
            dispatch(setLoading(false));
          }
        }else {
          const userData: UserData = {
            email: paymentData?.email! || email,
            first_name: paymentData?.first_name,
            last_name: paymentData?.last_name,
            settings: {
              paid_status: activeProduct?.paid_status,
              web_questions_answers: selectedAnswers,
            },
          };

          dispatch(subscribe({
            ...(paymentData || {}),
            three_d_secure_action_result_token_id: actionToken.id,
          }),)
            //@ts-ignore
            .then((purchase: Subscription) => {
              handlePaymentSuccess(purchase);
              APP_NAME === 'soula' && createUser(userData);
              setPaymentData(null);
            })
            .catch((error: { message: any; code: any }) => {
              setIsPaymentProcessing(false);
              toast(error?.message || "3D Secure failed");
              Analytics.trackEvent("ecommerce", "error", {
                type: "3ds",
                code: error?.code,
                decline_code: extractDeclineCode(error, paymentSystem),
              });
            });
        }
      },
      [isUpsaleOneTimePayment, oneTimeRecurlyId, dispatch, isOnlyOneTimePurshaseUpsale, subscribeRecurlyId, isRedirectToLastScreen, steps.length, paymentData, email, activeProduct?.paid_status, selectedAnswers, handlePaymentSuccess],
    );

    const onActionTokenError = useCallback(
      (error: RecurlyError) => {
        setTokenThreeDSecure(null);
        handleCardPaymentError({
          message: error.message,
          code: error.code,
          paymentSystem: PaymentSystem.RECURLY,
        });
      },
      [handleCardPaymentError],
    );

    const processFormData = useCallback(
      async (formData: any) => {
        submittedMethodRef.current = formData?.method || submittedMethodRef.current;
        Analytics.trackEvent("modal_payment", "submit", {
          method: formData?.method,
          paymentSystem: formData?.paymentSystem || paymentSystem,
          productId: activeProduct?.id,
          value: +activeProduct?.trialPriceAmount || +activeProduct?.amount,
          currency: activeProduct?.currency,
        });
        setPaymentData(formData);

        // Upsale retry with another card: the window was opened only to collect
        // a payment method. The offer screen charges it (it owns the amount, the
        // product and the payment-failed sheet), so stop here.
        if (isUpsaleOneTimePayment && onUpsaleCardCollected) {
          await onUpsaleCardCollected(formData);
          return undefined;
        }

        if (isUpsaleOneTimePayment && oneTimeRecurlyId) {
          try {
            dispatch(setLoading(true));
            const purchaseResult = await dispatch(
              handlePurchaseThunk({ recurlyId: oneTimeRecurlyId }),
            );

            if (purchaseResult.meta.requestStatus === "fulfilled") {
              if (isOnlyOneTimePurshaseUpsale) {
                isRedirectToLastScreen
                  ? dispatch(setCurrentStep(steps.length))
                  : dispatch(nextStep());
              }

              if (subscribeRecurlyId) {
                await dispatch(submitCancelSubscription(subscribeRecurlyId));
                await dispatch(
                  subscribe({ ...formData, price_id: subscribeRecurlyId }),
                ).then(() => {
                  dispatch(nextStep());
                });
                Analytics.trackEvent("step", "completed", {
                  upsaleID: "SKIP_TRIAL",
                  result: "upsale_selected",
                });
              }
            }
          } finally {
            dispatch(setLoading(false));
          }
        } else {
          const userData: UserData = {
            email: formData?.email! || email,
            first_name: formData?.first_name,
            last_name: formData?.last_name,
            settings: {
              paid_status: activeProduct?.paid_status,
              web_questions_answers: selectedAnswers,
            },
          };

          const effectivePaymentSystem = formData.paymentSystem || paymentSystem;
          const isStripe = effectivePaymentSystem === PaymentSystem.STRIPE;

          // TailoredPay: one request creates the trial charge and the
          // subscription together, and the server grants access — so no
          // client_secret dance and no separate intro payment.
          if (effectivePaymentSystem === PaymentSystem.TAILOREDPAY) {
            try {
              const purchase = (await dispatch(tailoredpayCheckout({
                product: activeProduct!,
                paymentToken: formData.payment_token,
                threeDS: formData.three_ds,
                email: formData.email || email,
                method: formData.method,
                billingAddress: formData.billing_address,
                paymentMethodKind: formData.payment_method_kind,
              }))) as unknown as Subscription;

              handlePaymentSuccess(purchase);
              APP_NAME === 'soula' && createUser(userData);
              return purchase;
            } catch (error: any) {
              // A misconfiguration (no plan on the gateway) is not a card
              // problem — don't force the card-issuer popup, which would tell the
              // user to update card details for something they cannot fix.
              handleCardPaymentError(error, {
                forcePopup: !error?.isConfigurationError,
              });
              return undefined;
            }
          }

          if (!isStripe) {
            dispatch(subscribe(formData))
              //@ts-ignore
              .then((purchase: Subscription) => {
                handlePaymentSuccess(purchase);
                APP_NAME === 'soula' && createUser(userData);
              })
              .catch((error: PaymentError & { data?: any }) => {
                if (error?.data?.three_d_secure_action_token_id) {
                  setTokenThreeDSecure(
                    error?.data?.three_d_secure_action_token_id,
                  );
                } else {
                  handleCardPaymentError(error);
                }
              });
            return undefined;
          }

          try {
            if (!formData.payment_method_id) {
              throw new Error('Stripe payment method is missing');
            }

            await dispatch(createCustomer({
              email: formData.email || email,
              paymentSystem: PaymentSystem.STRIPE,
            }));

            const introProduct = products.find((p) => isStripeIntroProduct(p));
            const introProductCode = introProduct?.productCode || STRIPE_INTRO_PAYMENT.productCode;
            const introProductId = introProduct?.id || STRIPE_INTRO_PAYMENT.productId;

            // A previous attempt already paid the trial and only the
            // subscription request failed — send just the subscription request
            // this time so the trial isn't charged twice.
            const introAlreadyPaid = !!readStripeIntroPaidMark(introProductCode);

            if (introAlreadyPaid) {
              Analytics.trackEvent('modal_payment', 'intro_charge_skipped', {
                place: errorPlace,
                productId: introProductId,
              });
            }

            let introPayment: any;
            if (!introAlreadyPaid) {
              try {
                introPayment = await dispatch(createOneClickPayment({
                  amount: stripeSubscriptionProduct?.trialPriceAmount || STRIPE_INTRO_PAYMENT.amount,
                  currency: STRIPE_INTRO_PAYMENT.currency as any,
                  productCode: introProductCode,
                  productId: introProductId,
                  paymentMethodId: formData.payment_method_id,
                  setupFutureUsage: 'off_session',
                  trigger: STRIPE_INTRO_PAYMENT.trigger,
                  metadata: {
                    product_id: introProductId,
                    price_id: introProduct?.id || STRIPE_INTRO_PAYMENT.priceId,
                  },
                }));
              } catch (oneTimeError: any) {
                // The one-time-payment request itself failed (HTTP 4xx/5xx) —
                // show the payment-error popup with the real backend error.
                clearSavedStripeSubscriptionId(getSavedStripeSubscriptionId());
                handleCardPaymentError(
                  buildStripePaymentError(
                    oneTimeError?.response?.data || oneTimeError,
                    oneTimeError?.message || 'Stripe intro payment request failed',
                    // Read off the error itself: what is handed to the builder
                    // is the response body, which no longer carries the status.
                    getHttpStatus(oneTimeError),
                  ),
                );
                return undefined;
              }

              const introClientSecret = getStripePaymentIntentClientSecret(introPayment);
              let introPaymentStatus = getStripePaymentIntentStatus(introPayment);
              let introPaymentIntentId: string | undefined;

              if (introClientSecret && introPayment?.requires_action) {
                const paymentIntent = await confirmStripePaymentIntent(introClientSecret);
                introPaymentStatus = paymentIntent?.status || introPaymentStatus;
                introPaymentIntentId = paymentIntent?.id;
              }

              if (!isSettledPaymentIntentStatus(introPaymentStatus)) {
                // One-time (intro) payment request failed — surface the real
                // backend error through the shared payment-error popup.
                const introError = buildStripePaymentError(
                  { ...(introPayment || {}), payment_intent_status: introPaymentStatus },
                  'Stripe intro payment was not completed',
                  // Off the unspread response: the interceptor keeps the status
                  // as a non-enumerable key, which the spread above drops.
                  getHttpStatus(introPayment),
                );
                handleCardPaymentError(introError);
                return undefined;
              }

              // The trial is charged. Remember it before touching the
              // subscription request, so a failure there — or a reload right
              // after it — can't lead to charging the trial twice.
              saveStripeIntroPaidMark(
                introProductCode,
                introPaymentIntentId || introPayment?.transaction_id || introPayment?.id,
              );
            }

            // The trial is paid from here on. A temporary glitch on the
            // subscription step is retried on its own so the user isn't asked to
            // click through a popup for something that fixes itself.
            const subscribeWithRetries = async (): Promise<Subscription> => {
              for (let attempt = 0; attempt <= SUBSCRIPTION_RETRY_ATTEMPTS; attempt += 1) {
                try {
                  return (await dispatch(
                    subscribe({
                      ...formData,
                      price_id: stripeSubscriptionProduct?.id || formData.price_id,
                      trial_period_days: String(activeProduct?.trialDays || STRIPE_INTRO_PAYMENT.trialPeriodDays),
                      skipSuccessHandling: true,
                    } as any),
                  )) as Subscription;
                } catch (subscribeError: any) {
                  const isLastAttempt = attempt === SUBSCRIPTION_RETRY_ATTEMPTS;
                  const isRetryable =
                    resolvePaymentErrorState(subscribeError, {
                      paymentSystem: PaymentSystem.STRIPE,
                      method: submittedMethodRef.current,
                    }) === PaymentErrorState.TEMPORARY;

                  if (isLastAttempt || !isRetryable) {
                    Analytics.trackEvent('modal_payment', 'subscription_failed_after_intro', {
                      attempts: attempt + 1,
                      intro_charged: !introAlreadyPaid,
                      place: errorPlace,
                      code: subscribeError?.code,
                    });
                    // Always state 3, whatever the reason. The trial is already
                    // paid, so "try again" is the honest instruction — the next
                    // attempt sends only the subscription request and the money
                    // isn't taken twice. Telling them their card was declined
                    // would send them looking for another card they don't need.
                    throw Object.assign(
                      subscribeError || new Error('Stripe subscription failed'),
                      { paymentErrorState: PaymentErrorState.TEMPORARY },
                    );
                  }

                  Analytics.trackEvent('modal_payment', 'subscription_retry', {
                    attempt: attempt + 1,
                    intro_charged: !introAlreadyPaid,
                    place: errorPlace,
                  });
                  await delay(SUBSCRIPTION_RETRY_DELAY_MS);
                }
              }

              throw new Error('Stripe subscription failed');
            };

            const subscriptionDetails = await subscribeWithRetries();

            const clientSecret = subscriptionDetails.client_secret || subscriptionDetails.clientSecret;
            const subscriptionId = getStripeSubscriptionId(subscriptionDetails);
            saveStripeSubscriptionId(subscriptionId);

            if (clientSecret && subscriptionDetails.requires_action) {
              // The bank's confirmation window just closed. What it returned
              // decides whether this is a purchase — showing success without
              // looking booked failed payments as sales.
              const paymentIntent = await confirmStripePaymentIntent(clientSecret);

              if (!isSettledPaymentIntentStatus(paymentIntent?.status)) {
                clearSavedStripeSubscriptionId(subscriptionId);
                handleCardPaymentError({
                  code: 'payment_intent_authentication_failure',
                  payment_intent_status: paymentIntent?.status,
                  message: 'Bank confirmation was not completed',
                  paymentSystem: PaymentSystem.STRIPE,
                } as any);
                return undefined;
              }

              subscriptionDetails.transaction_id = paymentIntent?.id || subscriptionDetails.transaction_id;
            }

            if (clientSecret || subscriptionDetails.requires_action) {
              // Wait for our own backend to confirm the subscription is on
              // before calling this a purchase.
              const confirmed = await dispatch(confirmStripeSubscriptionPayment(subscriptionId));
              clearSavedStripeSubscriptionId(subscriptionId);

              if (!confirmed) {
                handleCardPaymentError({
                  code: 'subscription_confirmation_failed',
                  message: 'Subscription payment was not confirmed',
                  paymentSystem: PaymentSystem.STRIPE,
                } as any, { state: PaymentErrorState.TEMPORARY });
                return undefined;
              }
            } else {
              clearSavedStripeSubscriptionId(subscriptionId);
            }

            // Subscription is on — the trial mark has done its job. Leaving it
            // behind would let a later purchase skip a trial charge it owes.
            clearStripeIntroPaidMark();

            const purchase = (await dispatch(handleSuccessPurchase(subscriptionDetails))) as Subscription;
            handlePaymentSuccess(purchase);
            APP_NAME === 'soula' && createUser(userData);
            return purchase;
          } catch (error: any) {
            clearSavedStripeSubscriptionId(getSavedStripeSubscriptionId());

            if (error?.data?.three_d_secure_action_token_id) {
              setTokenThreeDSecure(
                error?.data?.three_d_secure_action_token_id,
              );
            } else {
              handleCardPaymentError(error);
            }
            throw error;
          }
        }
      },
      [
        activeProduct?.amount,
        activeProduct?.currency,
        activeProduct?.id,
        activeProduct?.trialPriceAmount,
        activeProduct?.trialDays,
        dispatch,
        errorPlace,
        handleCardPaymentError,
        handlePaymentSuccess,
        isUpsaleOneTimePayment,
        onUpsaleCardCollected,
        paymentSystem,
        products,
        stripeSubscriptionProduct,
      ],
    );

    const handleSubmit = useCallback(
      async (formData: any) => {
        const ps = formData.paymentSystem || paymentSystem;
        if (formData.email && formData.email !== email) {
          dispatch(processEmail(formData.email));
        }
        if (formData.email || formData.first_name || formData.last_name) {
          Analytics.trackEvent("user", "info", {
            email: formData.email || undefined,
            first_name: formData.first_name || undefined,
            last_name: formData.last_name || undefined,
          });
        }
        if (ps === PaymentSystem.SOLIDGATE) {
          Analytics.trackEvent("modal_payment", "submit", {
            method: formData?.method,
            paymentSystem: formData?.paymentSystem || ps,
            productId: activeProduct?.id,
            value: +activeProduct?.trialPriceAmount || +activeProduct?.amount,
            currency: activeProduct?.currency,
          });
        }
        // Two cases skip /customers:
        //  - TailoredPay has no such step — the email is part of the
        //    /nmi/checkout body and the customer record lives in the gateway;
        //  - the upsale runs after the main purchase, so the customer already
        //    exists. Creating them again buys nothing and, when the billing auth
        //    source has drifted, throws "Missing billing auth token" and kills
        //    the charge before it starts.
        if (
          ps !== PaymentSystem.TAILOREDPAY &&
          !(isUpsaleOneTimePayment && onUpsaleCardCollected)
        ) {
          await dispatch(
            createCustomer({ email: formData.email, paymentSystem: ps }),
          );
        }
        let result;
        if (ps !== PaymentSystem.SOLIDGATE) {
          result = await processFormData(formData);
        }
        onSubmit && onSubmit();
        return result;
      },
      [
        activeProduct?.amount,
        activeProduct?.currency,
        activeProduct?.id,
        activeProduct?.trialPriceAmount,
        dispatch,
        email,
        isUpsaleOneTimePayment,
        onSubmit,
        onUpsaleCardCollected,
        paymentSystem,
        processFormData,
      ],
    );

    useImperativeHandle(ref, () => {
      return {
        showPaymentPopup() {
          setIsOpenPaymentPopup(true);
        },
        closePaymentPopup() {
          setIsOpenPaymentPopup(false);
          // Card / Apple Pay submits lock the page with the processing overlay;
          // when the caller closes the window to finish the charge itself, the
          // lock has to go with it or the overlay covers what comes next.
          setIsPaymentProcessing(false);
          if (isInjectedPaymentMethodOnPage) {
            dispatch(setInjectedPaymentMethod(defaultInjectedPaymentMethod));
          }
        },
        processFormData(data: any) {
          processFormData(data);
        },
        subscribeOnOneClick(data: SubscribeOnOneClickParams) {
          socRef.current?.subscribeOnOneClick(data);
        },
      };
    }, [
      defaultInjectedPaymentMethod,
      isInjectedPaymentMethodOnPage,
      dispatch,
      processFormData,
    ]);

    const handleApplePayClick = useCallback(() => {
      Analytics.trackEvent("apple_pay", "click", { paymentSystem });
    }, [paymentSystem]);

    const handleApplePaySubmit = useCallback(
      async (formData: any) => {
        submittedMethodRef.current = 'applepay';
        Analytics.trackEvent("apple_pay", "submit", {
          paymentSystem: formData?.paymentSystem || paymentSystem,
        });
        // Apple Pay sheet has been authorized & closed — lock the page now.
        setIsPaymentProcessing(true);
        await handleSubmit(formData);
        if (process.env.REACT_APP_APPLE_PAY_H2H) {
          const ps = formData.paymentSystem || paymentSystem;
          if (ps === PaymentSystem.SOLIDGATE) {
            dispatch(applePayOrder(formData))
              //@ts-ignore
              .then((purchase: Subscription) => {
                handlePaymentSuccess(purchase);
              })
              .catch((error: PaymentError) => {
                handleCardPaymentError(error);
              });
          }
        }
      },
      [
        dispatch,
        handleCardPaymentError,
        handlePaymentSuccess,
        handleSubmit,
        paymentSystem,
      ],
    );

    const handleGooglePayClick = useCallback(() => {
      Analytics.trackEvent("google_pay", "click", { paymentSystem });
    }, [paymentSystem]);

    const handleGooglePaySubmit = useCallback(
      (formData: any) => {
        submittedMethodRef.current = 'googlepay';
        Analytics.trackEvent("google_pay", "submit", {
          paymentSystem: formData?.paymentSystem || paymentSystem,
        });
        // Google Pay sheet has been authorized & closed — lock the page now.
        setIsPaymentProcessing(true);
        return handleSubmit(formData);
      },
      [handleSubmit, paymentSystem],
    );

    const handlePayPalSubmit = useCallback(
      async (formData: any) => {
        submittedMethodRef.current = 'paypal';
        setIsPaymentProcessing(true);
        await processFormData(formData);
      },
      [processFormData],
    );

    const handleUserInfoChange = useCallback(
      (info: any) => {
        const em = info?.email;
        if (em && em !== email) {
          dispatch(processEmail(em));
          dispatch(initOrders());
        }
        if (info?.zip) {
          dispatch(setPostCode(info.zip));
        }
      },
      [dispatch, email],
    );

    const handleCardFormSubmit = useCallback(
      (formData: any) => {
        submittedMethodRef.current = 'card';
        dispatch(setIsSubmitByCard(true));
        Analytics.trackEvent("card", "submit", {
          paymentSystem: formData?.paymentSystem || paymentSystem,
        });
        // Card submitted (incl. the bank 3-D Secure redirect that follows) —
        // lock the page so the form can't be submitted again.
        setIsPaymentProcessing(true);
        return handleSubmit(formData);
      },
      [dispatch, handleSubmit, paymentSystem],
    );

    // The × used to close the payment flow entirely and drop the user back on
    // the paywall, losing the card they had entered. Both the × and the button
    // now put them back in the payment window — leaving the payment is only
    // possible through the payment window's own ×.
    const handleErrorPopupCancel = useCallback(
      (isPaypal?: boolean) => {
        Analytics.trackEvent("error_popup", "cancel", {
          ...(isPaypal ? { mode: "paypal" } : {}),
          paymentSystem,
          error_state: errorState,
          place: errorPlace,
        });
        setIsOpenErrorPopup(false);
        setIsOpenPaymentPopup(true);
      },
      [errorPlace, errorState, paymentSystem],
    );

    const handleErrorPopupSubmit = useCallback(() => {
      setIsOpenErrorPopup(false);
      Analytics.trackEvent("error_popup", "submit", {
        paymentSystem,
        error_state: errorState,
        place: errorPlace,
      });
      setIsOpenPaymentPopup(true);
    }, [errorPlace, errorState, paymentSystem]);

    const handleSubscribeOnOneClickError = useCallback(
      (error: PaymentError) => {
        if (onSubscribeOnOneClickError) {
          onSubscribeOnOneClickError(error);
        } else {
          setErrorCode(error?.code);
          setErrorMessage(error?.message);
          setErrorState(
            resolvePaymentErrorState(error, {
              paymentSystem: error?.paymentSystem || paymentSystem,
              method: submittedMethodRef.current,
              place: errorPlace as 'purchase' | 'upsale',
            }),
          );
          setIsOpenErrorPopup(true);
        }
      },
      [errorPlace, onSubscribeOnOneClickError, paymentSystem],
    );

    return (
      <>
        {/* {isLoadingApp || isLoadingBilling
? (
          <Preloader className="PaymentModalPreloader" />
        )
: ( */}
          {stripeSubscriptionProduct && (
            <Provider>
              <BasePaymentModal
                activeProduct={stripeSubscriptionProduct}
                showPopup={!!isOpenPaymentPopup}
                onClose={handleClosePaymentPopup}
                onSuccess={handleCardPaymentSuccess}
                onError={handleCardPaymentError}
                options={options}
                showApplePay={!!showApplePay && !isOnlyCard && !isOnlyPayPal}
                showGooglePay={!!showGooglePay && !isOnlyCard && !isOnlyPayPal}
                showPayPal={showPayPal && !isOnlyCard && !hidePayPal}
                showMercadoPago={showMercadoPago}
                showCard={
                  isOnlyPayPal !== undefined
                    ? !isOnlyPayPal
                    : modeCardForm !== "disabled"
                }
                isOnlyBaseModal={useBasePaymentModal}
                title={paymentModalTitle}
                subtitle={paymentModalSubtitle}
                onApplePayClick={handleApplePayClick}
                onApplePaySubmit={handleApplePaySubmit}
                onGooglePayClick={handleGooglePayClick}
                onGooglePaySubmit={handleGooglePaySubmit}
                onPayPalSubmit={handlePayPalSubmit}
                onPayPalSuccess={handleCardPaymentSuccess}
                onPayPalError={handleCardPaymentError}
                onUserInfoChange={handleUserInfoChange}
                onSubmit={handleCardFormSubmit}
                fullMode={fullMode}
                isPurchaseProgress={isPurchaseProgress}
                upsaleTrigger={upsaleTrigger}
                onSaveTokenOneTime={onSaveTokenOneTime}
                snowModalOncePopup={showModalOnce}
              />
            </Provider>
          )}
        {/* )} */}

        {tokenThreeDSecure && (
          <ThreeDSecure
            actionTokenId={tokenThreeDSecure}
            onToken={onActionTokenExchanged}
            onError={onActionTokenError}
          />
        )}

        <SubscribeOnOneClick
          ref={socRef}
          onSuccess={onSuccess}
          onError={handleSubscribeOnOneClickError}
        />

        {/* The upsale owns its own payment-failed sheet — one message per
            failure, so the core popup must not surface there on top of it. */}
        {activeProduct && !isUpsaleOneTimePayment && (
          <ErrorPopup
            product={activeProduct}
            onClose={handleErrorPopupCancel}
            visible={isOpenErrorPopup}
            withPayPal={errorPopupWithPayPal}
            onPayPalSubmit={handlePayPalSubmit}
            onPayPalSuccess={handleCardPaymentSuccess}
            onPayPalError={handleCardPaymentError}
            onSubmit={handleErrorPopupSubmit}
            buttonTitle={errorPopup?.buttonTitle}
            statusCode={errorCode}
            errorMessage={errorMessage}
            errorState={errorState}
          />
        )}

        {showPaymentProcessingOverlay && (
          <PaymentProcessingOverlay visible={isPaymentProcessing} />
        )}
      </>
    );
  },
);
